// tauri-web.ts — the ONE web-safe Tauri surface. Every file that used to import `invoke`,
// `platform`, `listen`, or `convertFileSrc` from `@tauri-apps/*` imports them from HERE instead.
// On desktop/iOS these are the real Tauri APIs; in a browser they transparently fall back to the
// web runtime.
//
// This is what makes the SAME frontend run as web + macOS + iOS from one codebase: the UI,
// helpers, and brain calls don't change — only this boundary knows which surface it's on.

import {
  invoke as tauriInvoke,
  convertFileSrc as tauriConvertFileSrc,
} from "@tauri-apps/api/core";
import { listen as tauriListen, type EventCallback, type UnlistenFn } from "@tauri-apps/api/event";
import { platform as tauriPlatform } from "@tauri-apps/plugin-os";
import { isTauri, makeWebInvoke, WebStore } from "./web-runtime";
import { makeWebCommands } from "./web-commands";

const APP_SLUG = "interview-prep-tutor";

// One WebStore for the whole app (the browser's stand-in for native SQLite).
export const webStore = new WebStore(APP_SLUG);

export const invoke: typeof tauriInvoke = isTauri()
  ? tauriInvoke
  : (makeWebInvoke(APP_SLUG, webStore, makeWebCommands(webStore)) as typeof tauriInvoke);

/** Turn an on-device file path into a servable URL. Native uses the Tauri asset protocol;
 *  on web there are no on-device file paths, so the path is returned unchanged (harmless — the
 *  photo feature is native-only, so web image paths are always empty). */
export const convertFileSrc: typeof tauriConvertFileSrc = isTauri()
  ? tauriConvertFileSrc
  : (((filePath: string) => filePath) as typeof tauriConvertFileSrc);

/** Synchronous platform tag. Returns "web" in a browser; the real Tauri value on native. */
export function platform(): string {
  return isTauri() ? tauriPlatform() : "web";
}

/** Event listener. On web there are no native/deep-link events, so this is a no-op. */
export async function listen<T>(event: string, cb: EventCallback<T>): Promise<UnlistenFn> {
  if (isTauri()) return tauriListen<T>(event, cb);
  return async () => {};
}

// ── Web-safe file save (fleet fix 2026-08-27) ────────────────────────────────
// App code must never import plugin-dialog / plugin-fs directly (this file's header rule):
// in a browser those plugins throw (no __TAURI_INTERNALS__) inside untracked async blocks,
// so every export/save button died SILENTLY on web — found live in real-use QA (Expense's
// CSV/QuickBooks/Xero export did nothing). Native keeps the real dialog + file write; web
// turns the same two calls into a plain blob download.
import { save as _tauriSaveDialog, open as _tauriOpenDialog } from "@tauri-apps/plugin-dialog";
import { writeTextFile as _tauriWriteTextFile } from "@tauri-apps/plugin-fs";

export async function saveFileDialog(opts?: { defaultPath?: string }): Promise<string | null> {
  if (isTauri()) return _tauriSaveDialog(opts ?? {});
  // Web: no dialog exists — hand back the suggested filename; writeTextFile below downloads it.
  return opts?.defaultPath ?? "download.txt";
}

export async function openFileDialog(...args: Parameters<typeof _tauriOpenDialog>): Promise<Awaited<ReturnType<typeof _tauriOpenDialog>>> {
  if (isTauri()) return _tauriOpenDialog(...(args as []));
  return null; // web: file picking is an HTML <input type=file>; callers no-op on null
}

export async function writeTextFile(path: string, contents: string): Promise<void> {
  if (isTauri()) return _tauriWriteTextFile(path, contents);
  const name = String(path).split("/").pop() || "download.txt";
  const url = URL.createObjectURL(new Blob([contents], { type: "text/plain" }));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}
